var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import classNames from 'classnames';
import { useUXPContext } from '@core/context/useUXPContext';
import Button from '@widget/buttons/Button';
import { ProgressBar } from '@widget/loaders';
import NotificationBlock from '@helper/NotificationBlock';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import FileInput from '../FileInput/FileInput';
import MediaThumb from './MediaThumb';
import { deriveMediaType, formatFileSize, isCroppableImage, runFileCheck, useLibraryUpload } from './mediaLibrary';
import MediaCheckNotice from './MediaCheckNotice';
import ImageCropper from '@widget/other/ImageCropper/ImageCropper';
import { getUploadSizeLimitMB } from '@hooks/useFileUpload';
import { validateAllowedFileType } from '../FileInput/fileHelper';
// Single select: the drop-zone shows the chosen file (a new drop replaces it) with its check above;
// the host's header button uploads it and ends the dialog. The queue rows show while it uploads.
const SingleUpload = (props) => {
    const { allowedTypes, uploadPath, saveToLibrary, multiple, onPendingFilesChange, uploadRequestedAt, onUploaded, uploader, validate, checkKey, onBlockedChange, cropRules, onFixChange, fixInToolbar, } = props;
    const context = useUXPContext();
    const { uploadAllToLibrary, isUploading, progress } = useLibraryUpload({ uploadPath, saveToLibrary, uploader });
    // Step one: the files chosen and not yet sent (a file dropped on the field starts here)
    const [files, setFiles] = React.useState(() => (props.initialFiles || []).slice(0, 1));
    const [uploadError, setUploadError] = React.useState(null);
    // The chosen file being cropped
    const [cropFile, setCropFile] = React.useState(null);
    // Step two: the upload queue. null while choosing.
    const [queue, setQueue] = React.useState(null);
    const nextId = React.useRef(1);
    const busy = React.useRef(false);
    const mounted = React.useRef(true);
    // One object URL per chosen file. They are revoked whenever the list
    // changes and on unmount — a blob URL is held until it is released.
    const previews = React.useMemo(() => files.map(file => URL.createObjectURL(file)), [files]);
    React.useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews]);
    // The queue's thumbnails are released with their row, and all of them on unmount
    const queueRef = React.useRef(null);
    queueRef.current = queue;
    React.useEffect(() => () => {
        var _a;
        mounted.current = false;
        (_a = queueRef.current) === null || _a === void 0 ? void 0 : _a.forEach(r => URL.revokeObjectURL(r.preview));
    }, []);
    // The field's check per chosen file: a message, '' when fine, missing while checking
    const [problems, setProblems] = React.useState(new Map());
    const problemsRef = React.useRef(problems);
    problemsRef.current = problems;
    const lastCheckKey = React.useRef(checkKey);
    React.useEffect(() => {
        if (!validate) {
            setProblems(new Map());
            return;
        }
        let live = true;
        // Keep answers for files already checked, unless the rules changed
        const rulesChanged = lastCheckKey.current !== checkKey;
        lastCheckKey.current = checkKey;
        const known = rulesChanged ? new Map() : problemsRef.current;
        const next = new Map();
        files.forEach(f => { if (known.has(f))
            next.set(f, known.get(f)); });
        setProblems(next);
        files.filter(f => !next.has(f)).forEach(file => {
            runFileCheck(validate, file).then(problem => {
                if (!live)
                    return;
                setProblems(prev => new Map(prev).set(file, problem || ''));
            });
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, checkKey, validate]);
    const problemOf = (file) => validate ? problems.get(file) : '';
    const validFiles = files.filter(f => problemOf(f) === '');
    const checking = files.some(f => problemOf(f) === undefined);
    const blocked = checking || validFiles.length === 0;
    React.useEffect(() => {
        onBlockedChange === null || onBlockedChange === void 0 ? void 0 : onBlockedChange(files.length > 0 && blocked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files.length, blocked]);
    React.useEffect(() => () => onBlockedChange === null || onBlockedChange === void 0 ? void 0 : onBlockedChange(false), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // The refused file's crop, for a host that shows it in its toolbar
    const fixFile = files[0] && cropRules && problemOf(files[0]) && isCroppableImage(files[0].type) ? files[0] : null;
    React.useEffect(() => {
        onFixChange === null || onFixChange === void 0 ? void 0 : onFixChange(fixFile ? () => setCropFile(fixFile) : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixFile]);
    React.useEffect(() => () => onFixChange === null || onFixChange === void 0 ? void 0 : onFixChange(null), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // The host owns the single-mode upload button, so it needs the pending list.
    React.useEffect(() => {
        onPendingFilesChange === null || onPendingFilesChange === void 0 ? void 0 : onPendingFilesChange(files);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);
    // Switching tabs unmounts this view — its button must not linger in the header.
    React.useEffect(() => () => onPendingFilesChange === null || onPendingFilesChange === void 0 ? void 0 : onPendingFilesChange([]), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // Only a *change* uploads, so mounting with a timestamp already set (the
    // host keeps one across tab switches) does nothing.
    const lastRequestRef = React.useRef(uploadRequestedAt || 0);
    React.useEffect(() => {
        if (!uploadRequestedAt || uploadRequestedAt === lastRequestRef.current)
            return;
        lastRequestRef.current = uploadRequestedAt;
        uploadPending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadRequestedAt]);
    function acceptFiles(picked, allValid) {
        if (!picked.length)
            return;
        if (!allValid) {
            const maxSizeMB = getUploadSizeLimitMB(context);
            const tooBig = maxSizeMB > 0 && picked.some(f => f.size > maxSizeMB * 1024 * 1024);
            setUploadError(tooBig
                ? context.$L('uxp-core.form-error.file-too-large', { maxSizeMB })
                : context.$L('uxp-core.form-error.invalid-file'));
            return;
        }
        setUploadError(null);
        setFiles(prev => multiple ? [...prev, ...picked] : picked.slice(0, 1));
    }
    function onFileSelected(file, isValid) {
        // No file: deleted from the preview, or a refused drop (the drop-zone says why)
        if (!file) {
            setFiles([]);
            return;
        }
        acceptFiles([file], isValid);
    }
    function removeFile(index) {
        setFiles(prev => prev.filter((f, i) => i !== index));
    }
    function updateRow(id, change) {
        if (!mounted.current)
            return;
        setQueue(prev => prev ? prev.map(r => r.id === id ? Object.assign(Object.assign({}, r), change) : r) : prev);
    }
    /** Upload these queue rows one after another, each row showing its own state */
    function runUpload(rows) {
        return __awaiter(this, void 0, void 0, function* () {
            if (busy.current || !rows.length)
                return;
            busy.current = true;
            try {
                const { urls } = yield uploadAllToLibrary(rows.map(r => r.file), {
                    onFileStart: (file, i) => updateRow(rows[i].id, { status: 'uploading', message: undefined }),
                    onFileDone: (file, i, result) => updateRow(rows[i].id, result.error === undefined
                        ? { status: 'done' }
                        : { status: 'failed', message: result.error }),
                });
                // Single select ends the dialog here; multiple adds to the selection
                if (urls.length)
                    onUploaded(urls);
            }
            finally {
                busy.current = false;
            }
        });
    }
    function uploadPending() {
        return __awaiter(this, void 0, void 0, function* () {
            if (busy.current || isUploading || !files.length || blocked)
                return;
            setUploadError(null);
            // Files that failed the check are left out
            const rows = validFiles.map(file => ({
                id: nextId.current++,
                file,
                preview: URL.createObjectURL(file),
                status: 'waiting',
            }));
            setQueue(rows);
            setFiles([]);
            yield runUpload(rows);
        });
    }
    function retryRow(row) {
        updateRow(row.id, { status: 'waiting', message: undefined });
        runUpload([row]);
    }
    function removeRow(row) {
        URL.revokeObjectURL(row.preview);
        setQueue(prev => {
            const next = (prev || []).filter(r => r.id !== row.id);
            // Nothing left: back to choosing files
            return next.length ? next : null;
        });
    }
    /** Back to step one, to choose more files */
    function addMore() {
        queue === null || queue === void 0 ? void 0 : queue.forEach(r => URL.revokeObjectURL(r.preview));
        setQueue(null);
    }
    const removeLabel = context.$L('uxp-core.text.remove');
    /** "Crop and resize" for a chosen image that failed the check */
    function cropButton(file) {
        if (!cropRules || !problemOf(file) || !isCroppableImage(file.type))
            return null;
        return _jsx(Button, { title: context.$L('uxp-core.image-cropper.crop-and-resize'), leftIcon: "fal crop-simple", variant: "secondary", size: "small", disabled: isUploading, onClick: () => setCropFile(file) });
    }
    // The cropped file takes the chosen one's place, and is checked again
    const cropper = cropRules && _jsx(ImageCropper, { show: !!cropFile, file: cropFile || undefined, rules: cropRules, onApply: (cropped) => {
            setFiles(prev => prev.map(f => f === cropFile ? cropped : f));
            setCropFile(null);
        }, onCancel: () => setCropFile(null), onChooseAnother: () => {
            setFiles(prev => prev.filter(f => f !== cropFile));
            setCropFile(null);
        } });
    function statusText(row) {
        switch (row.status) {
            case 'waiting': return context.$L('uxp-core.media-picker.status-waiting');
            case 'uploading': return progress ? `${progress}%` : context.$L('uxp-core.media-picker.status-uploading');
            case 'done': return context.$L('uxp-core.media-picker.status-uploaded');
            default: return context.$L('uxp-core.media-picker.upload-failed');
        }
    }
    function renderRow(file, preview, row, onRemove) {
        const status = row === null || row === void 0 ? void 0 : row.status;
        return _jsxs("div", { className: classNames('uxpcore_mediapicker__filerow', status && `uxpcore_mediapicker__filerow--${status}`, {
                'uxpcore_mediapicker__filerow--failed': !row && !!problemOf(file),
            }), children: [_jsx("div", { className: "uxpcore_mediapicker__filerow-thumb", children: _jsx(MediaThumb, { value: preview, name: file.name, type: deriveMediaType(file.type) }) }), _jsxs("div", { className: "uxpcore_mediapicker__filerow-info", children: [_jsx("div", { className: "uxpcore_mediapicker__filerow-name", title: file.name, children: file.name }), _jsxs("div", { className: "uxpcore_mediapicker__filerow-meta", children: [_jsx("span", { children: formatFileSize(file.size) }), row && _jsx("span", { className: "uxpcore_mediapicker__filerow-status", children: statusText(row) })] }), status === 'uploading' &&
                            _jsx(ProgressBar, { value: progress, indeterminate: !progress, size: "small" }), status === 'failed' &&
                            _jsx("div", { className: "uxpcore_mediapicker__filerow-error", children: row.message || context.$L('uxp-core.media-picker.upload-failed') }), !row && problemOf(file) &&
                            _jsx("div", { className: "uxpcore_mediapicker__filerow-error", children: problemOf(file).split('\n').join(' ') })] }), status === 'done' &&
                    _jsx("span", { className: "uxpcore_mediapicker__filerow-done", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'check-circle'] }) }), (onRemove || status === 'failed') &&
                    _jsxs("div", { className: "uxpcore_mediapicker__filerow-actions", children: [!row && cropButton(file), status === 'failed' &&
                                _jsx(Button, { title: context.$L('uxp-core.text.try-again'), leftIcon: "fal redo", variant: "secondary", size: "small", disabled: isUploading, onClick: () => retryRow(row) }), onRemove &&
                                _jsx(Button, { title: removeLabel, variant: "secondary", size: "small", disabled: isUploading, onClick: onRemove })] })] }, row ? row.id : `${file.name}-${preview}`);
    }
    const errorBlock = uploadError &&
        _jsx(NotificationBlock, { class: "uxpcore_mediapicker__notice", variant: "danger", mode: "compact", layout: "bordered", icon: true, message: uploadError });
    // ---- Step two: the queue, no drop-zone ----
    if (queue) {
        const doneCount = queue.filter(r => r.status === 'done').length;
        const failedCount = queue.filter(r => r.status === 'failed').length;
        const running = isUploading || queue.some(r => r.status === 'uploading' || r.status === 'waiting');
        return _jsxs("div", { className: "uxpcore_mediapicker__upload uxpcore_mediapicker__upload--queue", children: [!running && failedCount > 0 &&
                    _jsx(NotificationBlock, { class: "uxpcore_mediapicker__notice", variant: "danger", mode: "compact", layout: "bordered", icon: true, message: context.$L('uxp-core.media-picker.upload-failed-count', { count: failedCount }) }), _jsx("div", { className: "uxpcore_mediapicker__filelist", children: queue.map(row => renderRow(row.file, row.preview, row, row.status === 'failed' ? () => removeRow(row) : undefined)) }), !running && failedCount === 0 &&
                    _jsxs("div", { className: "uxpcore_mediapicker__upload-summary", children: [_jsx("span", { className: "uxpcore_mediapicker__upload-summary-text", children: context.$L(multiple ? 'uxp-core.media-picker.uploaded-added' : 'uxp-core.media-picker.uploaded-count', { count: doneCount }) }), _jsx(Button, { title: context.$L('uxp-core.media-picker.add-more-files'), leftIcon: "fal plus", variant: "secondary", size: "small", onClick: addMore })] })] });
    }
    // ---- Step one: choose files ----
    return _jsxs("div", { className: classNames('uxpcore_mediapicker__upload', {
            'uxpcore_mediapicker__upload--has-files': multiple && files.length > 0,
        }), children: [errorBlock, !multiple && files[0] && problemOf(files[0]) && _jsx(MediaCheckNotice, { message: fixInToolbar && cropButton(files[0]) ? `${problemOf(files[0])} ${context.$L('uxp-core.media-picker.crop-below')}` : problemOf(files[0]), action: fixInToolbar ? undefined : cropButton(files[0]) }), _jsx(FileInput, { className: "uxpcore_mediapicker__upload-zone", value: multiple ? undefined : files[0], allowedTypes: allowedTypes, maxSizeMB: getUploadSizeLimitMB(context), multiple: multiple, onChange: onFileSelected, onFilesChange: acceptFiles, dropAreaIcon: ['fal', 'cloud-upload'], disabled: isUploading, dropzone: true, removable: true, fullWidth: true }), multiple && files.length > 0 && _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_mediapicker__filelist", children: files.map((file, index) => renderRow(file, previews[index], undefined, () => removeFile(index))) }), _jsx("div", { className: "uxpcore_mediapicker__upload-buttons", children: _jsx(Button, { className: "uxpcore_mediapicker__upload-action", title: validFiles.length === 1
                                ? context.$L('uxp-core.media-picker.upload-one-file')
                                : context.$L('uxp-core.media-picker.upload-n-files', { count: validFiles.length }), leftIcon: "fal cloud-upload", variant: "primary", size: "small", disabled: isUploading || blocked, onClick: uploadPending }) })] }), cropper] });
};
// Multiple select: the focused file shows in the drop-zone (a new drop adds files), and every file
// sits in the strip under it, first the fixed "New upload" tile. A file uploads as soon as it is
// added and passes its check, and joins the selection; one that fails waits for a crop. Files can
// only be added and deleted.
const MultiUpload = (props) => {
    const { allowedTypes, uploadPath, saveToLibrary, uploader, validate, checkKey, cropRules, onUploaded, onRemoved, localOnly, onReadyChange } = props;
    const context = useUXPContext();
    const { uploadAllToLibrary, progress } = useLibraryUpload({ uploadPath, saveToLibrary, uploader });
    const maxSizeMB = getUploadSizeLimitMB(context);
    const [entries, setEntries] = React.useState([]);
    const entriesRef = React.useRef(entries);
    entriesRef.current = entries;
    // The file in the preview; null = "New upload" (the empty drop-zone)
    const [focusId, setFocusId] = React.useState(null);
    const [cropId, setCropId] = React.useState(null);
    const nextId = React.useRef(1);
    const queue = React.useRef([]);
    const pumping = React.useRef(false);
    const mounted = React.useRef(true);
    React.useEffect(() => () => {
        mounted.current = false;
        entriesRef.current.forEach(e => URL.revokeObjectURL(e.preview));
    }, []);
    function update(id, change) {
        if (!mounted.current)
            return;
        setEntries(prev => prev.map(e => e.id === id ? Object.assign(Object.assign({}, e), change) : e));
    }
    /** Upload the waiting files one after another */
    function pump() {
        return __awaiter(this, void 0, void 0, function* () {
            if (pumping.current)
                return;
            pumping.current = true;
            try {
                while (queue.current.length && mounted.current) {
                    const id = queue.current.shift();
                    const entry = entriesRef.current.find(e => e.id === id);
                    if (!entry)
                        continue;
                    update(id, { status: 'uploading', message: undefined });
                    yield uploadAllToLibrary([entry.file], {
                        onFileDone: (file, i, result) => {
                            if (!entriesRef.current.some(e => e.id === id))
                                return;
                            if (result.url) {
                                update(id, { status: 'done', url: result.url });
                                onUploaded([result.url]);
                            }
                            else
                                update(id, { status: 'failed', message: result.error });
                        },
                    });
                }
            }
            finally {
                pumping.current = false;
            }
        });
    }
    function enqueue(id) {
        update(id, { status: 'waiting', message: undefined });
        queue.current.push(id);
        pump();
    }
    function check(id, file) {
        return __awaiter(this, void 0, void 0, function* () {
            const problem = validate ? yield runFileCheck(validate, file) : '';
            if (!mounted.current || !entriesRef.current.some(e => e.id === id))
                return;
            if (problem)
                update(id, { status: 'problem', message: problem });
            // Checking only: a file that passes is ready as it is
            else if (localOnly)
                update(id, { status: 'done' });
            else
                enqueue(id);
        });
    }
    function add(files) {
        const allowed = files.filter(f => validateAllowedFileType(f.type, allowedTypes || [], f.name) && !(maxSizeMB > 0 && f.size > maxSizeMB * 1024 * 1024));
        if (!allowed.length)
            return;
        const added = allowed.map(file => ({ id: nextId.current++, file, preview: URL.createObjectURL(file), status: 'checking' }));
        entriesRef.current = [...entriesRef.current, ...added];
        setEntries(entriesRef.current);
        setFocusId(added[0].id);
        added.forEach(e => check(e.id, e.file));
    }
    /** A file dropped on the focused one's preview takes its place, and is checked (and uploaded) again */
    function replace(entry, file) {
        if (!validateAllowedFileType(file.type, allowedTypes || [], file.name) || (maxSizeMB > 0 && file.size > maxSizeMB * 1024 * 1024))
            return;
        URL.revokeObjectURL(entry.preview);
        queue.current = queue.current.filter(id => id !== entry.id);
        if (entry.url)
            onRemoved === null || onRemoved === void 0 ? void 0 : onRemoved(entry.url);
        entriesRef.current = entriesRef.current.map(e => e.id === entry.id
            ? Object.assign(Object.assign({}, e), { file, preview: URL.createObjectURL(file), status: 'checking', message: undefined, url: undefined }) : e);
        setEntries(entriesRef.current);
        check(entry.id, file);
    }
    function remove(entry) {
        URL.revokeObjectURL(entry.preview);
        queue.current = queue.current.filter(id => id !== entry.id);
        entriesRef.current = entriesRef.current.filter(e => e.id !== entry.id);
        setEntries(entriesRef.current);
        if (focusId === entry.id)
            setFocusId(null);
        if (entry.url)
            onRemoved === null || onRemoved === void 0 ? void 0 : onRemoved(entry.url);
    }
    // Files dropped on the field: added once, as if dropped here
    React.useEffect(() => {
        var _a;
        if ((_a = props.initialFiles) === null || _a === void 0 ? void 0 : _a.length)
            add(props.initialFiles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // The rules changed: check again the files that have not uploaded yet
    const lastCheckKey = React.useRef(checkKey);
    React.useEffect(() => {
        if (lastCheckKey.current === checkKey)
            return;
        lastCheckKey.current = checkKey;
        entriesRef.current.filter(e => e.status === 'problem').forEach(e => { update(e.id, { status: 'checking' }); check(e.id, e.file); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkKey]);
    // Checking only: the host hears which files are ready
    React.useEffect(() => {
        if (localOnly)
            onReadyChange === null || onReadyChange === void 0 ? void 0 : onReadyChange(entries.filter(e => e.status === 'done').map(e => e.file));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);
    const focused = entries.find(e => e.id === focusId);
    const cropEntry = entries.find(e => e.id === cropId);
    // The cropped file takes the entry's place and is checked again
    const cropper = cropRules && _jsx(ImageCropper, { show: !!cropEntry, file: cropEntry === null || cropEntry === void 0 ? void 0 : cropEntry.file, rules: cropRules, onApply: (cropped) => {
            const entry = cropEntry;
            setCropId(null);
            if (!entry)
                return;
            URL.revokeObjectURL(entry.preview);
            const preview = URL.createObjectURL(cropped);
            entriesRef.current = entriesRef.current.map(e => e.id === entry.id ? Object.assign(Object.assign({}, e), { file: cropped, preview, status: 'checking', message: undefined }) : e);
            setEntries(entriesRef.current);
            check(entry.id, cropped);
        }, onCancel: () => setCropId(null), onChooseAnother: () => { if (cropEntry)
            remove(cropEntry); setCropId(null); } });
    const cropAction = focused && cropRules && isCroppableImage(focused.file.type) && _jsx(Button, { title: context.$L('uxp-core.image-cropper.crop-and-resize'), leftIcon: "fal crop-simple", variant: "secondary", size: "small", onClick: () => setCropId(focused.id) });
    function statusHint(entry) {
        switch (entry.status) {
            case 'checking': return context.$L('uxp-core.media-picker.status-waiting');
            case 'waiting': return context.$L('uxp-core.media-picker.status-waiting');
            case 'uploading': return progress ? `${progress}%` : context.$L('uxp-core.media-picker.status-uploading');
            case 'done': return context.$L(localOnly ? 'uxp-core.media-picker.status-ready' : 'uxp-core.media-picker.status-uploaded');
            case 'problem': return context.$L('uxp-core.media-picker.not-selected-until-fixed');
            default: return context.$L('uxp-core.media-picker.upload-failed');
        }
    }
    const removeLabel = context.$L('uxp-core.text.remove');
    return _jsxs("div", { className: "uxpcore_mediapicker__upload uxpcore_mediapicker__upload--multi", children: [(focused === null || focused === void 0 ? void 0 : focused.status) === 'problem' && _jsx(MediaCheckNotice, { message: focused.message, action: cropAction }), (focused === null || focused === void 0 ? void 0 : focused.status) === 'failed' &&
                _jsx(NotificationBlock, { class: "uxpcore_mediapicker__notice", variant: "danger", mode: "compact", layout: "bordered", icon: true, message: focused.message || context.$L('uxp-core.media-picker.upload-failed'), action: _jsx(Button, { title: context.$L('uxp-core.text.try-again'), leftIcon: "fal redo", variant: "secondary", size: "small", onClick: () => enqueue(focused.id) }) }), _jsx(FileInput, { className: "uxpcore_mediapicker__upload-zone", value: focused === null || focused === void 0 ? void 0 : focused.file, allowedTypes: allowedTypes, maxSizeMB: maxSizeMB, 
                // On a file: a drop replaces it. On "New upload" (empty): files are added.
                multiple: !focused, onChange: (file) => {
                    if (!focused)
                        return;
                    if (file)
                        replace(focused, file);
                    else
                        remove(focused);
                }, onFilesChange: (files) => add(files), dropAreaIcon: ['fal', 'cloud-upload'], dropzone: true, removable: !!focused, previewInfo: focused && `${focused.file.name} · ${statusHint(focused)}`, fullWidth: true }), _jsxs("div", { className: "uxpcore_mediapicker__uploadstrip", role: "list", children: [_jsxs("button", { type: "button", className: classNames('uxpcore_mediapicker__uploadstrip-new', { 'uxpcore_mediapicker__uploadstrip-new--active': !focused }), onClick: () => setFocusId(null), children: [_jsx(FontAwesomeIconWrapper, { icon: ['fal', 'plus'] }), _jsx("span", { children: context.$L('uxp-core.media-picker.new-upload') })] }), entries.map(entry => _jsxs("div", { role: "listitem", tabIndex: 0, title: entry.file.name, className: classNames('uxpcore_mediapicker__uploadstrip-tile', `uxpcore_mediapicker__uploadstrip-tile--${entry.status}`, {
                            'uxpcore_mediapicker__uploadstrip-tile--active': entry.id === focusId,
                        }), onClick: () => setFocusId(entry.id), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setFocusId(entry.id);
                        } }, children: [_jsx(MediaThumb, { value: entry.preview, name: entry.file.name, type: deriveMediaType(entry.file.type) }), entry.status === 'done' && _jsx("span", { className: "uxpcore_mediapicker__uploadstrip-badge uxpcore_mediapicker__uploadstrip-badge--done", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'check'] }) }), (entry.status === 'problem' || entry.status === 'failed') && _jsx("span", { className: "uxpcore_mediapicker__uploadstrip-badge uxpcore_mediapicker__uploadstrip-badge--error", children: "!" }), (entry.status === 'uploading' || entry.status === 'waiting' || entry.status === 'checking') &&
                                _jsx("span", { className: "uxpcore_mediapicker__uploadstrip-progress", children: _jsx(ProgressBar, { value: entry.status === 'uploading' ? progress : 0, indeterminate: entry.status !== 'uploading' || !progress, size: "small" }) }), _jsx("button", { type: "button", className: "uxpcore_mediapicker__uploadstrip-remove", "aria-label": removeLabel, title: removeLabel, onClick: (e) => { e.stopPropagation(); remove(entry); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'xmark'] }) })] }, entry.id))] }), cropper] });
};
/**
 * @export
 * The picker's upload view. Single select: the drop-zone shows the chosen file (a new drop replaces it)
 * with its check and Crop above it, and the host's header button uploads it and ends the dialog.
 * Multiple select: the focused file shows in the drop-zone (a new drop adds files) over a strip of every
 * file, first a fixed "New upload" tile; each file uploads as soon as it passes its check and joins the
 * selection, and files can only be added and deleted. Files go to the content store and, when enabled,
 * are recorded in the user's media library.
 *
 * @advanced
 */
const UploadView = (props) => props.multiple ? _jsx(MultiUpload, Object.assign({}, props)) : _jsx(SingleUpload, Object.assign({}, props));
export default UploadView;
